import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Play, Plus, Trash2, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ROLE_LABEL = { both: "Semua", beauty_creator: "Beauty Creator", affiliator: "Affiliator" };
const ytId = (url) => { const m = (url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/); return m ? m[1] : null; };
const driveId = (url) => { const m = (url || "").match(/drive\.google\.com\/file\/d\/([\w-]+)/); return m ? m[1] : null; };
const embedUrl = (url) => { const y = ytId(url); if (y) return `https://www.youtube.com/embed/${y}`; const d = driveId(url); if (d) return `https://drive.google.com/file/d/${d}/preview`; return url; };
const thumbFor = (v) => { if (v.thumbnail) return v.thumbnail; const y = ytId(v.video_url); return y ? `https://img.youtube.com/vi/${y}/hqdefault.jpg` : null; };

export default function VideoblogPanel({ editable = false }) {
  const [items, setItems] = useState([]);
  const [playing, setPlaying] = useState(null);
  const load = useCallback(() => api.get("/videoblogs").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { try { await api.delete(`/videoblogs/${id}`); load(); } catch (e) { toast.error("Gagal menghapus"); } };

  return (
    <div data-testid="videoblog-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><Clapperboard className="w-5 h-5 text-showty-red" /> Videoblog</h3>
          <p className="text-sm text-muted-foreground">{editable ? "Bagikan video edukasi & update untuk KOL dan Affiliator." : "Belajar & update lewat video pilihan dari tim Showtynion."}</p>
        </div>
        {editable && <AddVideoblogDialog onDone={load} />}
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="videoblog-empty">
          <Clapperboard className="w-6 h-6 mx-auto mb-2 text-showty-red/50" /> Belum ada videoblog
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="videoblog-list">
          {items.map((v) => {
            const thumb = thumbFor(v);
            return (
              <div key={v.id} className="rounded-2xl border border-border bg-white overflow-hidden flex flex-col" data-testid={`videoblog-${v.id}`}>
                <button onClick={() => setPlaying(v)} className="relative h-40 bg-showty-navy/90 group" data-testid={`videoblog-play-${v.id}`}>
                  {thumb ? <img src={thumb} alt={v.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" /> : <div className="w-full h-full flex items-center justify-center"><Clapperboard className="w-8 h-8 text-white/60" /></div>}
                  <span className="absolute inset-0 flex items-center justify-center"><span className="w-12 h-12 rounded-full bg-showty-red/90 flex items-center justify-center"><Play className="w-5 h-5 text-white ml-0.5" /></span></span>
                </button>
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {v.category && <span className="text-[10px] uppercase tracking-wide font-semibold text-showty-gold">{v.category}</span>}
                      {editable && <span className="text-[10px] rounded-full bg-showty-cream border border-border px-2 py-0.5 text-showty-navy">{ROLE_LABEL[v.target_role]}</span>}
                    </div>
                    {editable && <button onClick={() => remove(v.id)} className="text-muted-foreground hover:text-destructive" data-testid={`videoblog-delete-${v.id}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <h4 className="mt-1.5 font-display text-lg text-showty-navy leading-snug">{v.title}</h4>
                  {v.description && <p className="mt-1 text-sm text-muted-foreground line-clamp-2 flex-1">{v.description}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="max-w-2xl" data-testid="videoblog-player">
          <DialogHeader><DialogTitle className="font-display text-xl text-showty-navy">{playing?.title}</DialogTitle></DialogHeader>
          {playing && (
            <div className="aspect-video w-full rounded-xl overflow-hidden bg-black">
              <iframe title={playing.title} src={embedUrl(playing.video_url)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}
          {playing?.description && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{playing.description}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AddVideoblogDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const empty = { title: "", description: "", video_url: "", thumbnail: "", website_url: "", target_role: "both", category: "" };
  const [f, setF] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const upload = async (e, key) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/media/upload", fd); set(key, r.data.url); toast.success("File terupload"); }
    catch { toast.error("Gagal upload"); }
    setUploading(false); e.target.value = "";
  };
  const save = async () => {
    if (!f.title.trim()) return toast.error("Judul wajib diisi");
    if (!f.video_url.trim()) return toast.error("Upload video atau isi link video");
    try {
      await api.post("/videoblogs", f);
      toast.success("Videoblog ditambahkan");
      setOpen(false); setF(empty); onDone?.();
    } catch (e) { toast.error("Gagal menambah videoblog"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="videoblog-add-btn"><Plus className="w-4 h-4 mr-1" /> Tambah Video</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="videoblog-add-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">Tambah Videoblog</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Judul</Label><Input className="mt-1" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="videoblog-title" /></div>
          <div>
            <Label>Upload Video</Label>
            <Input type="file" accept="video/*" className="mt-1" onChange={(e) => upload(e, "video_url")} data-testid="videoblog-upload" />
            {uploading && <p className="text-xs text-showty-red mt-1">Mengupload…</p>}
          </div>
          <div><Label>Atau Link Video (YouTube / Google Drive)</Label><Input className="mt-1" placeholder="https://youtube.com/watch?v=… atau https://drive.google.com/file/d/…" value={f.video_url} onChange={(e) => set("video_url", e.target.value)} data-testid="videoblog-url" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kategori (opsional)</Label><Input className="mt-1" placeholder="Tips Konten / Live Selling" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="videoblog-category" /></div>
            <div><Label>Untuk</Label>
              <Select value={f.target_role} onValueChange={(v) => set("target_role", v)}>
                <SelectTrigger className="mt-1" data-testid="videoblog-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Semua (Creator & Affiliator)</SelectItem>
                  <SelectItem value="beauty_creator">Beauty Creator</SelectItem>
                  <SelectItem value="affiliator">Affiliator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Upload Thumbnail (opsional)</Label>
            <Input type="file" accept="image/*" className="mt-1" onChange={(e) => upload(e, "thumbnail")} data-testid="videoblog-thumb-upload" />
            {f.thumbnail && <img src={f.thumbnail} alt="thumb" className="mt-2 h-24 w-full object-cover rounded-lg border border-border" />}
          </div>
          <div><Label>URL Website (opsional)</Label><Input className="mt-1" placeholder="https://showtynion.com/..." value={f.website_url} onChange={(e) => set("website_url", e.target.value)} data-testid="videoblog-website" /></div>
          <div><Label>Deskripsi</Label><Textarea rows={4} className="mt-1" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="videoblog-desc" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={uploading} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="videoblog-save">Publikasikan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
